'use client';

import { useState, useRef } from 'react';
import Image from 'next/image';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { SectionHeading } from '@/components/shared/SectionHeading';
import { fadeInUp } from '@/lib/animations';
import { cn } from '@/lib/utils';
import type { SolutionItem } from '@/lib/content-schema';

interface Props {
  solutions: SolutionItem[];
}

export function SolutionSection({ solutions }: Props) {
  const [activeTab, setActiveTab] = useState(solutions[0]?.id ?? '');
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const activeSolution = solutions.find((s) => s.id === activeTab) ?? solutions[0];

  return (
    <section id="soluciones" className="py-24 md:py-32 bg-zibarit-bone relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          title="Soluciones"
        />

        {/* Tab selector */}
        <motion.div
          ref={ref}
          variants={fadeInUp}
          initial="initial"
          animate={isInView ? 'animate' : 'initial'}
          className="flex flex-wrap justify-center gap-3 mb-12 md:mb-16"
        >
          {solutions.map((solution) => (
            <button
              key={solution.id}
              onClick={() => setActiveTab(solution.id)}
              className={cn(
                'relative px-5 py-2.5 rounded-full text-sm font-lato font-bold uppercase tracking-wider transition-all duration-300',
                activeTab === solution.id
                  ? 'bg-zibarit-verde-oscuro text-zibarit-bone'
                  : 'border border-zibarit-marron-secundario/30 text-zibarit-marron-oscuro hover:border-zibarit-verde-oscuro/50',
              )}
            >
              {solution.label}
            </button>
          ))}
        </motion.div>

        {/* Tab content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeSolution.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
          >
            {/* Text content */}
            <div>
              <h3 className="text-title text-2xl md:text-3xl lg:text-4xl text-zibarit-negro mb-4">
                {activeSolution.headline}
              </h3>
              <p className="text-body text-zibarit-marron-oscuro mb-8 leading-relaxed">
                {activeSolution.description}
              </p>
              <ul className="space-y-3">
                {activeSolution.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-zibarit-verde-oscuro/15 flex items-center justify-center shrink-0 mt-0.5">
                      <svg
                        className="w-3 h-3 text-zibarit-verde-oscuro"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={3}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-body text-sm text-zibarit-negro/80 leading-snug">
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>
              {activeSolution.footnote && (
                <p className="text-body text-xs text-zibarit-marron-oscuro/60 mt-6 leading-relaxed">
                  {activeSolution.footnote}
                </p>
              )}
            </div>

            {/* Visual placeholder */}
            <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-zibarit-verde-oscuro/10 to-zibarit-verde-brillante/10 border border-zibarit-verde-oscuro/10 aspect-[4/3] flex items-center justify-center">
              <TabVisual tabId={activeSolution.id} />
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}

function TabVisual({ tabId }: { tabId: string }) {
  if (tabId === 'difusion') {
    return (
      <div className="p-8 w-full space-y-4">
        <div className="flex items-center gap-3 p-4 bg-white rounded-xl shadow-sm">
          <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
            <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" />
            </svg>
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-zibarit-negro">WhatsApp Club</p>
            <p className="text-xs text-zibarit-marron-oscuro/60">15K+ miembros activos</p>
          </div>
          <span className="text-xs font-mono font-bold text-green-600 bg-green-50 px-2 py-1 rounded-full">ACTIVO</span>
        </div>
        <div className="flex items-center gap-3 p-4 bg-white rounded-xl shadow-sm">
          <div className="w-10 h-10 rounded-full bg-pink-500/20 flex items-center justify-center">
            <svg className="w-5 h-5 text-pink-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0z" />
            </svg>
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-zibarit-negro">Instagram</p>
            <p className="text-xs text-zibarit-marron-oscuro/60">Stories + posts</p>
          </div>
          <span className="text-xs font-mono font-bold text-pink-600 bg-pink-50 px-2 py-1 rounded-full">ACTIVO</span>
        </div>
        <div className="flex items-center gap-3 p-4 bg-white rounded-xl shadow-sm">
          <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
            <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
            </svg>
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-zibarit-negro">Embajadores Z</p>
            <p className="text-xs text-zibarit-marron-oscuro/60">Influencers verificados</p>
          </div>
          <span className="text-xs font-mono font-bold text-blue-600 bg-blue-50 px-2 py-1 rounded-full">ACTIVO</span>
        </div>
      </div>
    );
  }

  return (
    <Image
      src="/images/mock-ticketing.png"
      alt="Mock del dashboard de ticketing de Zibarit"
      fill
      className="object-cover scale-130"
    />
  );
}
